import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck, Bot, BookOpen, Wallet,
  ShieldCheck, Lock, Building2, MapPin, Globe, AlertTriangle,
  Mail, KeyRound, ArrowLeft
} from "lucide-react";
import { T } from "../constants";

export default function LoginPage({ onSendOTP, onVerifyOTP, lang, setLang, subdomain }) {
  const t = T[lang];
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  const { school, loading: schoolLoading, error: schoolError, isSubdomain } = subdomain || {};

  // Start countdown for resend OTP
  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async () => {
    setErr("");
    if (!email.trim()) {
      setErr(lang === "en" ? "Please enter your email address." : "कृपया अपना ईमेल दर्ज करें।");
      return;
    }
    setLoading(true);
    try {
      await onSendOTP(email);
      setOtpSent(true);
      startCountdown();
    } catch (e) {
      setErr(e.message || (lang === "en" ? "Failed to send OTP." : "OTP भेजने में विफल।"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setErr("");
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setErr(lang === "en" ? "Please enter the 6-digit OTP." : "कृपया 6 अंकों का OTP दर्ज करें।");
      return;
    }
    setLoading(true);
    try {
      await onVerifyOTP(email, otpString);
    } catch (e) {
      setErr(e.message || (lang === "en" ? "Invalid OTP." : "गलत OTP।"));
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Only keep last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      handleVerifyOTP();
    }
  };

  const handleBackToEmail = () => {
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setErr("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !otpSent) handleSendOTP();
  };

  const features = [
    { icon: <ClipboardCheck size={20} />, title: "Attendance", desc: "Real-time tracking with auto-alerts", color: "#4F46E5" },
    { icon: <Bot size={20} />, title: "AI Exams", desc: "Auto-generate papers & grade instantly", color: "#E8690A" },
    { icon: <BookOpen size={20} />, title: "Academics", desc: "Curriculum, logs & performance analytics", color: "#059669" },
    { icon: <Wallet size={20} />, title: "Fees", desc: "Complete billing, reminders & reports", color: "#7C3AED" },
  ];

  if (isSubdomain && schoolLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#FAFBFE]">
        <div className="text-center">
          <span className="w-8 h-8 border-3 border-navy-200 border-t-navy-600 rounded-full animate-spin inline-block mb-4" />
          <p className="text-navy-400 font-sora text-sm">Loading school portal...</p>
        </div>
      </div>
    );
  }

  if (isSubdomain && schoolError === "school_not_found") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#FAFBFE]">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
            <AlertTriangle size={28} className="text-amber-500" />
          </div>
          <h2 className="font-sora font-extrabold text-2xl text-navy-800 mb-2">School Not Found</h2>
          <p className="text-navy-400 text-sm leading-relaxed mb-6">
            The school portal <strong className="text-navy-600">{subdomain.slug}.edupulseai.co</strong> does not exist or is inactive.
          </p>
          <a
            href={`${window.location.protocol}//app.edupulseai.co.in`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-purple text-white text-sm font-semibold font-sora no-underline"
          >
            Go to Main Portal
          </a>
        </div>
      </div>
    );
  }

  const showSchoolBranding = isSubdomain && school;

  return (
    <div className="fixed inset-0 flex font-body">
      {/* Left branding panel */}
      <div className="hidden lg:flex w-[55%] gradient-dark relative overflow-hidden flex-col justify-between p-12 xl:p-16">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none blur-[100px]"
          style={{ background: "radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none blur-[80px]"
          style={{ background: "radial-gradient(circle, rgba(232,105,10,0.06) 0%, transparent 60%)" }} />
        <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,rgba(255,255,255,.012)_0px,rgba(255,255,255,.012)_1px,transparent_1px,transparent_48px)] pointer-events-none" />

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3.5 mb-16">
              <div className="w-12 h-12 rounded-xl gradient-purple flex items-center justify-center text-white font-sora font-extrabold text-base shadow-glow">
                EP
              </div>
              <div className="font-sora font-extrabold text-[24px] text-white leading-none">
                eduPulse<span className="text-brand-400">AI</span>
              </div>
            </div>

            {showSchoolBranding ? (
              <>
                <h1 className="font-sora font-extrabold text-[40px] xl:text-[48px] text-white leading-[1.08] max-w-[480px]">
                  {school.name}
                </h1>
                <div className="flex flex-col gap-2 mt-6">
                  {school.city && (
                    <div className="flex items-center gap-2 text-white/40 text-[14px]">
                      <MapPin size={14} />
                      <span>{[school.city, school.state].filter(Boolean).join(", ")}</span>
                    </div>
                  )}
                  {school.board && (
                    <div className="flex items-center gap-2 text-white/40 text-[14px]">
                      <Building2 size={14} />
                      <span>{school.board} Board</span>
                    </div>
                  )}
                  {school.website && (
                    <div className="flex items-center gap-2 text-white/40 text-[14px]">
                      <Globe size={14} />
                      <span>{school.website.replace(/^https?:\/\//, "")}</span>
                    </div>
                  )}
                </div>
                <p className="text-[15px] text-white/25 leading-[1.8] mt-8 max-w-[420px]">
                  {lang === "en"
                    ? "Access your school portal for attendance, fees, exams, and performance analytics."
                    : "उपस्थिति, शुल्क, परीक्षा और प्रदर्शन विश्लेषण के लिए अपने स्कूल पोर्टल तक पहुँचें।"}
                </p>
              </>
            ) : (
              <>
                <h1 className="font-sora font-extrabold text-[44px] xl:text-[52px] text-white leading-[1.08] max-w-[480px]">
                  Smarter Schools.
                  <br />
                  <span className="text-gradient-brand">Happier Parents.</span>
                </h1>
                <p className="text-[16px] text-white/35 leading-[1.8] mt-6 max-w-[420px]">
                  {lang === "en"
                    ? "One platform for attendance, fees, exams, performance analytics and AI-powered test generation."
                    : "उपस्थिति, शुल्क, परीक्षा, प्रदर्शन विश्लेषण और AI-संचालित टेस्ट जनरेशन के लिए एक प्लेटफ़ॉर्म।"}
                </p>
              </>
            )}
          </motion.div>
        </div>

        <div className="relative z-10">
          {!showSchoolBranding && (
            <div className="grid grid-cols-2 gap-3">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-3.5 p-4 rounded-2xl bg-white/[.03] border border-white/[.05]
                             transition-all duration-300 hover:bg-white/[.06] hover:border-white/[.1]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                >
                  <div
                    className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center mt-0.5"
                    style={{ background: `${f.color}15`, border: `1px solid ${f.color}20`, color: f.color }}
                  >
                    {f.icon}
                  </div>
                  <div>
                    <div className="text-[13px] text-white/80 font-semibold font-sora">{f.title}</div>
                    <div className="text-[11px] text-white/30 mt-0.5 leading-relaxed">{f.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {showSchoolBranding && (
            <div className="grid grid-cols-2 gap-3">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-3 p-3.5 rounded-2xl bg-white/[.03] border border-white/[.05]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
                >
                  <div
                    className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                    style={{ background: `${f.color}15`, color: f.color }}
                  >
                    {f.icon}
                  </div>
                  <div className="text-[12px] text-white/50 font-medium font-sora">{f.title}</div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-8 text-white/15 text-[11px]">
            <ShieldCheck size={13} />
            <span>256-bit SSL encryption &middot; SOC 2 compliant &middot; GDPR ready</span>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-[#FAFBFE] relative overflow-y-auto">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(79,70,229,0.04) 0%, transparent 55%)" }} />
        <div className="absolute -bottom-16 -left-16 w-52 h-52 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,105,10,0.03) 0%, transparent 55%)" }} />

        <motion.div
          className="w-full max-w-[420px] relative"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {/* Mobile logo + school branding */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            {showSchoolBranding && school.logo ? (
              <img src={school.logo} alt={school.name} className="w-10 h-10 rounded-xl object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-xl gradient-purple flex items-center justify-center text-white font-sora font-extrabold text-sm shadow-glow">
                EP
              </div>
            )}
            <div className="font-sora font-extrabold text-[20px] text-navy-800 leading-none">
              {showSchoolBranding ? school.name : (
                <>eduPulse<span className="text-brand-400">AI</span></>
              )}
            </div>
          </div>

          {/* Language toggle */}
          <div className="flex gap-1 justify-end mb-6">
            {["en", "hi"].map(l => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-lg border-none text-xs font-bold cursor-pointer font-sora
                            transition-all duration-200
                            ${lang === l
                              ? "bg-navy-800 text-white shadow-sm"
                              : "bg-transparent text-navy-300 hover:text-navy-500 hover:bg-navy-50"}`}
              >
                {l === "en" ? "EN" : "हि"}
              </button>
            ))}
          </div>

          {showSchoolBranding && (
            <motion.div
              className="flex items-center gap-3 p-4 rounded-2xl bg-navy-50/60 border border-navy-100 mb-6"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {school.logo ? (
                <img src={school.logo} alt="" className="w-11 h-11 rounded-xl object-cover hidden lg:block" />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 hidden lg:flex items-center justify-center text-white font-sora font-bold text-sm">
                  {school.name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-sora font-bold text-[14px] text-navy-700 truncate hidden lg:block">{school.name}</div>
                <div className="text-[12px] text-navy-400 mt-0.5">
                  {[school.board, school.city].filter(Boolean).join(" · ")}
                </div>
              </div>
            </motion.div>
          )}

          <div className="mb-8">
            <h2 className="font-sora text-[30px] font-extrabold text-navy-800 leading-tight">
              {showSchoolBranding
                ? (lang === "en" ? "Sign in to your portal" : "अपने पोर्टल में साइन इन करें")
                : t.welcomeBack}
            </h2>
            <p className="text-[15px] text-navy-400 mt-2">
              {showSchoolBranding
                ? (lang === "en" ? "Enter your school credentials to continue" : "जारी रखने के लिए अपना स्कूल क्रेडेंशियल दर्ज करें")
                : t.loginSubtitle}
            </p>
          </div>

          <div className="flex flex-col gap-5">
            {/* OTP Flow - Step 1: Email */}
            {!otpSent ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <div>
                  <label className="input-label">{t.email}</label>
                  <div className="relative">
                    <input
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={lang === "en" ? "you@yourschool.in" : "आपका ईमेल"}
                      className="input-field !pl-12"
                      autoFocus
                      autoComplete="email"
                    />
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy-400" size={20} />
                  </div>
                  <p className="text-[12px] text-navy-400 mt-2">
                    {lang === "en" 
                      ? "We'll send a 6-digit OTP to your email" 
                      : "हम आपके ईमेल पर 6 अंकों का OTP भेजेंगे"}
                  </p>
                </div>
              </motion.div>
            ) : (
              /* OTP Flow - Step 2: OTP Input */
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={handleBackToEmail}
                    className="p-2 rounded-lg hover:bg-navy-50 transition-colors"
                    disabled={loading}
                  >
                    <ArrowLeft size={20} className="text-navy-600" />
                  </button>
                  <span className="text-navy-600 text-sm">{email}</span>
                </div>

                <label className="input-label">
                  {lang === "en" ? "Enter OTP" : "OTP दर्ज करें"}
                </label>
                <div className="flex gap-3 justify-between mb-4">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(index, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-bold rounded-xl border-2 border-navy-200
                                 focus:border-navy-500 focus:outline-none transition-colors
                                 text-navy-800 bg-white"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
                <p className="text-[12px] text-navy-400">
                  {lang === "en" 
                    ? "Enter the 6-digit code sent to your email" 
                    : "अपने ईमेल पर भेजा गया 6 अंकों का कोड दर्ज करें"}
                </p>
              </motion.div>
            )}

            <AnimatePresence>
              {err && (
                <motion.div
                  className="text-accent-red text-sm font-semibold bg-accent-red-light p-3.5 rounded-xl border border-red-200"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Send OTP Button */}
            {!otpSent && (
              <motion.button
                onClick={handleSendOTP}
                disabled={loading}
                className="w-full py-4 px-6 rounded-xl border-none text-white
                           text-base font-bold font-sora cursor-pointer
                           gradient-purple shadow-glow
                           transition-all duration-200 disabled:opacity-60"
                whileHover={!loading ? { scale: 1.01, y: -1 } : {}}
                whileTap={!loading ? { scale: 0.98 } : {}}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {lang === "en" ? "Sending..." : "भेज रहे हैं..."}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <KeyRound size={18} />
                    {lang === "en" ? "Send OTP" : "OTP भेजें"}
                  </span>
                )}
              </motion.button>
            )}

            {/* Verify OTP Button */}
            {otpSent && (
              <>
                <motion.button
                  onClick={handleVerifyOTP}
                  disabled={loading}
                  className="w-full py-4 px-6 rounded-xl border-none text-white
                             text-base font-bold font-sora cursor-pointer
                             gradient-purple shadow-glow
                             transition-all duration-200 disabled:opacity-60"
                  whileHover={!loading ? { scale: 1.01, y: -1 } : {}}
                  whileTap={!loading ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t.signing}
                    </span>
                  ) : t.login}
                </motion.button>

                {/* Resend OTP */}
                <div className="text-center">
                  <button
                    onClick={handleSendOTP}
                    disabled={countdown > 0 || loading}
                    className="bg-transparent border-none text-accent-purple text-[13px] font-semibold cursor-pointer font-body hover:underline p-0 disabled:text-navy-300 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {countdown > 0 
                      ? (lang === "en" ? `Resend OTP in ${countdown}s` : `${countdown} सेकंड में फिर से भेजें`) 
                      : (lang === "en" ? "Resend OTP" : "OTP फिर से भेजें")}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-10 text-navy-300 text-[12px]">
            <Lock size={12} />
            <span>{lang === "en" ? "Secured with 256-bit encryption" : "256-बिट एन्क्रिप्शन से सुरक्षित"}</span>
          </div>

          <p className="text-center text-[11px] text-navy-200 mt-6">
            Powered by <span className="font-sora font-bold text-navy-400">eduPulse<span className="text-brand-400">AI</span></span>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
